import { useCallback, useContext, useSyncExternalStore } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import type { WebMeta } from "@/shared/contracts";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";

/**
 * 宠物照片缩略档（UI-PET-PHOTO-SRCSET-01，6c2b 10-13；后端是 P 的 PET-PHOTO-SIZES-01，契约 I 10-13 定）。
 * 起因：门牌上 32px 的头像原来下载的是两三 MB 的原图。
 * 后端三条照片地址（成员照片、公开照片、官方形象）各加了可选的 w：
 * - 只收 96、192、384 三档，闭集外回 422；
 * - 回短边等于 w 的小图，比例不变、不放大；
 * - 权限、缓存头和原图一样；生成不了就回原图。
 * /meta 的能力表里 pets.photo_sizes 是 available 才带 w；不带 w 的老地址照常能用。
 */
export const PHOTO_WIDTHS = [96, 192, 384] as const;
export type PhotoWidth = (typeof PHOTO_WIDTHS)[number];

/**
 * 按显示边长（px）选一档，照 I 10-13 的写法：16–48px 用 96，48–96px 用 192，更大的用 384。
 * 2 倍屏上至少 2 倍清晰，只有大于 192px 的才低于 2 倍。
 * 不用 srcset 让浏览器挑：r1 实测 Chrome 按相邻两档的中点挑，64px 头像在 2.25 倍屏取了 96，只有 1.5 倍、发虚（见包 README）。
 */
export function photoWidthFor(size: number): PhotoWidth {
  return size <= 48 ? 96 : size <= 96 ? 192 : 384;
}

/**
 * 只认后端给的这三种地址：
 * - /api/v1/web/media/pets/{id}/photo
 * - /api/v1/web/public/media/pets/{id}/photo
 * - /api/v1/web/public/identity/{id}/official/{rev}
 * 可以带查询串（成员照片带 ?v= 版本），不能带 #、不能有空白。
 * 其余地址一律不动：演示小灰猫、物种插画、外站、别的照片路由等。
 */
const SIZED_PHOTO = /^\/api\/v1\/web\/(?:(?:public\/)?media\/pets\/[^/?#\s]+\/photo|public\/identity\/[^/?#\s]+\/official\/[^/?#\s]+)(?:\?[^#\s]*)?$/;

/**
 * 给照片地址带上这一档的 w：已有查询串用 &，没有用 ?，末尾是空 ? 就直接接。
 * 不是这三种地址，或者地址里已经带了 w，原样返回，照旧用原图。
 */
export function sizedPhotoSrc(src: string, size: number): string {
  if (!SIZED_PHOTO.test(src)) return src;
  const at = src.indexOf("?");
  if (at >= 0 && new URLSearchParams(src.slice(at + 1)).has("w")) return src;
  const sep = at < 0 ? "?" : at === src.length - 1 ? "" : "&";
  return `${src}${sep}w=${photoWidthFor(size)}`;
}

const PHOTO_SIZES = "pets.photo_sizes";
const idle = () => () => {};

/**
 * 能不能带 w：只在 live 下，全站共用的 /meta 缓存（queryKeys.meta）里 pets.photo_sizes 是 available 时为真。
 * - 只被动读缓存，不另发请求：两种外壳（RootLayout、BareLayout）的 ModeRibbon 都会取 /meta。
 * - 没回来、取不到、没有这一项、演示模式，一律为假，头像和原来一模一样。
 * - 回包没有 capabilities、capabilities 不是数组、里面的元素不是对象：也按假算，不抛错。
 *   r2 写的是 capabilities.some，回包缺这一项时整页渲染出错；SIZES-02 的回归查出，r3 改。
 * - 用 QueryClientContext，不用 useQueryClient：外面没有 QueryClientProvider 时（单独渲染头像的用例）不抛错，按假算。
 * - /meta 晚到也会跟着变（订阅查询缓存）。
 */
export function usePhotoSizes(): boolean {
  const client = useContext(QueryClientContext);
  const subscribe = useCallback((onChange: () => void) => (client ? client.getQueryCache().subscribe(onChange) : idle()), [client]);
  const read = () => {
    if (env.dataMode !== "live" || !client) return false;
    const capabilities: unknown = client.getQueryData<WebMeta>(queryKeys.meta)?.capabilities;
    return Array.isArray(capabilities) && capabilities.some((c) => c?.key === PHOTO_SIZES && c?.status === "available");
  };
  return useSyncExternalStore(subscribe, read, read);
}
